<template>
  <div>
    <MoleculeDrillDown
      :opened="showForm && !isLgBreakpoint && !disabled"
      :title="t('gettingTheRv')"
      :value="computedDestinationLabel"
      :placeholder="computedDestinationPlaceholder"
      :disabled="disabled || hideEditButton"
      :badge="hasIncompleteDestination ? t('required') : undefined"
      test-id="change-destination"
      @open="openDestinationPicker"
      @close="closeDestinationPicker"
    >
      <EstimateRvUsage />

      <div class="mt-auto flex justify-end">
        <TButton @click="handleApply(closeDestinationPicker)">
          {{ t('actions.apply') }}
        </TButton>
      </div>
    </MoleculeDrillDown>

    <ClientOnly v-if="!disabled">
      <TModalInfo
        v-if="isLgBreakpoint"
        v-model:visible="showForm"
        :title="t('gettingTheRv')"
        :label-primary="t('actions.apply')"
        enable-scroll
        @action:primary="(dismiss) => handleApply(dismiss)"
      >
        <EstimateRvUsage />
      </TModalInfo>

      <!-- Prevent flickering on page load -->
      <template #fallback />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { VALIDATION_SCOPE_RTB_DESTINATION } from '~/constants/rv'

defineProps<{
  hideEditButton?: boolean
  disabled?: boolean
}>()

const { t } = useI18n()
const { isLgBreakpoint } = useBreakpoint()
const {
  rvHasDelivery,
  rvIsDeliveryOnly,
  rvFormattedLocation,
} = useRvDetails()
const { bookingEstimateParams, hasIncompleteDestination, selectedRvUsage } = useBookingEstimate()

const canSelectRvUsage = computed(() => {
  return rvHasDelivery.value && !rvIsDeliveryOnly.value
})

const computedDestinationLabel = computed(() => {
  if (hasIncompleteDestination.value) {
    return
  }

  switch (selectedRvUsage.value) {
    case 'Pickup':
      return t('rvz28661SimplifiedRtbFlow.pickup.title', { location: rvFormattedLocation.value })
    case 'Delivery':
    case 'StationaryAfterDelivery':
      return t('rvz28661SimplifiedRtbFlow.delivery.title', { location: bookingEstimateParams.value.destination?.name })
    default:
      return ''
  }
})

const computedDestinationPlaceholder = computed(() => {
  if (canSelectRvUsage.value) {
    return t('selectAnOption')
  }
  return t('selectDestination')
})

const showForm = ref(false)
function openDestinationPicker() {
  showForm.value = true
}

function closeDestinationPicker() {
  showForm.value = false
}

const { r$ } = useCollectScope(VALIDATION_SCOPE_RTB_DESTINATION)

function handleApply(dismiss: () => void) {
  if (r$.$invalid) {
    r$.$touch()
    return
  }

  dismiss()
}

defineExpose({
  openDestinationPicker,
})
</script>

<i18n src="~~/i18n/locales/common/booking/destination.json" lang="json" />

<i18n lang="json">
  {
    "en": {
      "selectAnOption": "Select an option",
      "selectDestination": "Select a destination",
      "gettingTheRv": "Getting the RV"
    },
    "fr": {
      "selectAnOption": "Sélectionner une option",
      "selectDestination": "Sélectionner une destination",
      "gettingTheRv": "Récupération du VR"
    }
  }
</i18n>
